// Service API sécurisé pour communiquer avec le backend
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Classe pour gérer les erreurs API avec plus de détails
export class APIError extends Error {
    constructor(message, status, data = null, code = null) {
        super(message);
        this.name = 'APIError';
        this.status = status;
        this.data = data;
        this.code = code;
    }
}

// Utilitaire pour faire les requêtes HTTP sécurisées
const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Configuration par défaut avec credentials pour les cookies.
    // Pour un envoi multipart (FormData), le navigateur fixe lui-même le
    // Content-Type avec la frontière ; on ne doit pas l'imposer.
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const config = {
        credentials: 'include', // IMPORTANT: Inclure les cookies httpOnly
        headers: {
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            'X-Requested-With': 'XMLHttpRequest', // Protection CSRF
            ...options.headers,
        },
        ...options,
    };

    // Pas besoin de gérer manuellement les tokens - les cookies sont automatiques
    try {
        const response = await fetch(url, config);
        
        // Gérer les réponses non-JSON (par exemple, 204 No Content)
        let data = null;
        if (response.headers.get('content-type')?.includes('application/json')) {
            data = await response.json();
        }

        if (!response.ok) {
            // Extraire les informations d'erreur du backend
            const errorMessage = data?.message || 'Erreur API';
            const errorCode = data?.code || null;
            const errorData = data || null;
            
            throw new APIError(errorMessage, response.status, errorData, errorCode);
        }

        return data;
    } catch (error) {
        if (error instanceof APIError) {
            throw error;
        }
        
        // Erreurs réseau ou autres
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            throw new APIError(
                'Impossible de se connecter au serveur. Vérifiez votre connexion internet.',
                0,
                { originalError: error.message },
                'NETWORK_ERROR'
            );
        }
        
        throw new APIError(
            'Erreur inattendue: ' + error.message,
            0,
            { originalError: error.message },
            'UNKNOWN_ERROR'
        );
    }
};

// Service d'authentification sécurisé
export const authService = {
    // Connexion avec gestion automatique des tokens
    async login(email, password) {
        const data = await apiRequest('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });
        
        // Les tokens sont automatiquement stockés dans les cookies httpOnly
        return data.data;
    },

    // Inscription
    async register(email, password) {
        const data = await apiRequest('/auth/register', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });
        
        return data.data;
    },

    // Déconnexion avec révocation côté serveur
    async logout() {
        try {
            await apiRequest('/auth/logout', {
                method: 'POST',
            });
        } catch (error) {
            console.error('Erreur lors de la déconnexion:', error);
            // Même si l'appel API échoue, les cookies seront supprimés côté serveur
        }
    },

    // Déconnexion globale (toutes les sessions)
    async logoutAll() {
        try {
            await apiRequest('/auth/logout-all', {
                method: 'POST',
            });
        } catch (error) {
            console.error('Erreur lors de la déconnexion globale:', error);
        }
    },

    // Récupérer les informations de l'utilisateur connecté
    async getCurrentUser() {
        try {
            const data = await apiRequest('/auth/me');
            return data.data?.user;
        } catch (error) {
            // Si l'utilisateur n'est pas connecté ou token expiré
            if (error.status === 401) {
                return null;
            }
            throw error;
        }
    },

    // Renouveler les tokens (normalement géré automatiquement)
    async refreshTokens() {
        try {
            const data = await apiRequest('/auth/refresh', {
                method: 'POST',
            });
            return data.data?.user;
        } catch (error) {
            // Si le refresh échoue, l'utilisateur doit se reconnecter
            console.error('Échec du refresh des tokens:', error);
            return null;
        }
    },

    // Vérifier si l'utilisateur semble connecté (tente une requête)
    async isAuthenticated() {
        try {
            const user = await this.getCurrentUser();
            return !!user;
        } catch (error) {
            return false;
        }
    },

    // Changer le mot de passe (révoque toutes les autres sessions)
    async changePassword(currentPassword, newPassword) {
        const data = await apiRequest('/auth/change-password', {
            method: 'POST',
            body: JSON.stringify({ currentPassword, newPassword }),
        });
        return data;
    },

    // Récupérer les sessions actives
    async getSessions() {
        const data = await apiRequest('/auth/sessions');
        return data.data?.sessions || [];
    }
};

// Service des transactions avec gestion d'erreurs améliorée
export const transactionService = {
    // Récupérer un mois avec ses transactions
    async getMonth(monthKey) {
        try {
            const data = await apiRequest(`/transactions/months/${monthKey}`);
            return data.data?.month;
        } catch (error) {
            // Gestion spécifique des erreurs
            if (error.code === 'VERSION_CONFLICT') {
                console.warn('Conflit de version détecté, rechargement nécessaire');
            }
            throw error;
        }
    },

    // Ajouter une transaction
    async addTransaction(monthKey, transaction) {
        try {
            const data = await apiRequest(`/transactions/months/${monthKey}`, {
                method: 'POST',
                body: JSON.stringify(transaction),
            });
            return {
                transaction: data.data?.transaction,
                monthStats: data.data?.monthStats
            };
        } catch (error) {
            // Gestion des erreurs spécifiques
            if (error.code === 'INVALID_AMOUNT') {
                throw new Error('Le montant saisi n\'est pas valide. Vérifiez le format.');
            }
            if (error.code === 'RATE_LIMIT_TRANSACTIONS') {
                throw new Error('Trop de transactions créées rapidement. Attendez quelques minutes.');
            }
            throw error;
        }
    },

    // Modifier une transaction
    async updateTransaction(transactionId, transaction) {
        try {
            const data = await apiRequest(`/transactions/${transactionId}`, {
                method: 'PUT',
                body: JSON.stringify(transaction),
            });
            return {
                transaction: data.data?.transaction,
                monthStats: data.data?.monthStats,
                suggestRule: data.data?.suggestRule || null
            };
        } catch (error) {
            if (error.code === 'TRANSACTION_NOT_FOUND') {
                throw new Error('Cette transaction n\'existe plus. La page va être rechargée.');
            }
            throw error;
        }
    },

    // Supprimer une transaction
    async deleteTransaction(transactionId) {
        try {
            const data = await apiRequest(`/transactions/${transactionId}`, {
                method: 'DELETE',
            });
            return {
                monthStats: data.data?.monthStats
            };
        } catch (error) {
            if (error.code === 'TRANSACTION_NOT_FOUND') {
                throw new Error('Cette transaction n\'existe plus.');
            }
            throw error;
        }
    },

    // Mettre à jour le solde initial d'un mois
    async updateInitialBalance(monthKey, initialBalance) {
        try {
            const data = await apiRequest(`/transactions/months/${monthKey}/initial-balance`, {
                method: 'PUT',
                body: JSON.stringify({ initialBalance }),
            });
            return data.data?.monthStats;
        } catch (error) {
            if (error.code === 'RATE_LIMIT_CALCULATIONS') {
                throw new Error('Trop de modifications du solde. Attendez quelques minutes.');
            }
            throw error;
        }
    },

    // Vérifier la santé du service
    async healthCheck() {
        const data = await apiRequest('/transactions/health');
        return data.data;
    }
};

// Service des templates récurrents
export const recurringService = {
    // Récupérer tous les templates
    async getTemplates() {
        const data = await apiRequest('/recurring');
        return data.data?.templates || [];
    },

    // Récupérer seulement les templates actifs
    async getActiveTemplates() {
        const data = await apiRequest('/recurring/active');
        return data.data?.templates || [];
    },

    // Créer un template
    async createTemplate(template) {
        const data = await apiRequest('/recurring', {
            method: 'POST',
            body: JSON.stringify(template),
        });
        return data.data?.template;
    },

    // Récupérer un template spécifique
    async getTemplate(templateId) {
        const data = await apiRequest(`/recurring/${templateId}`);
        return data.data?.template;
    },

    // Modifier un template
    async updateTemplate(templateId, template) {
        const data = await apiRequest(`/recurring/${templateId}`, {
            method: 'PUT',
            body: JSON.stringify(template),
        });
        return data.data?.template;
    },

    // Supprimer un template
    async deleteTemplate(templateId) {
        const data = await apiRequest(`/recurring/${templateId}`, {
            method: 'DELETE',
        });
        return data;
    },

    // Activer/désactiver un template
    async toggleTemplate(templateId) {
        const data = await apiRequest(`/recurring/${templateId}/toggle`, {
            method: 'PUT',
        });
        return data.data?.template;
    }
};

// Service utilisateur
export const userService = {
    // Récupérer le profil
    async getProfile() {
        const data = await apiRequest('/users/profile');
        return data.data?.user;
    },

    // Mettre à jour le profil
    async updateProfile(userData) {
        const data = await apiRequest('/users/profile', {
            method: 'PUT',
            body: JSON.stringify(userData),
        });
        return data.data?.user;
    },

    // Supprimer le compte
    async deleteAccount(password) {
        const data = await apiRequest('/users/account', {
            method: 'DELETE',
            body: JSON.stringify({ password }),
        });
        return data;
    }
};

const IMPORT_ERROR_MESSAGES = {
    LLM_NOT_CONFIGURED: 'L\'import nécessite un accès à Claude côté serveur (clé API ou LLM_PROVIDER=agent).',
    LLM_AUTH: 'Accès Claude refusé côté serveur : clé invalide, ou session « claude login » expirée.',
    LLM_BILLING: 'Le compte Anthropic n\'a plus de crédits : recharge-le sur console.anthropic.com.',
    LLM_RATE_LIMIT: 'Le service de libellés est saturé, réessaie dans quelques instants.',
    LLM_TIMEOUT: 'Le service de libellés n\'a pas répondu à temps, réessaie.',
    LLM_ERROR: 'Le service de libellés est indisponible pour le moment.',
    IMPORT_IN_PROGRESS: 'Un import est déjà en cours, patiente quelques secondes.',
    PREVIEW_EXPIRED: 'L\'aperçu a expiré, relance l\'analyse du fichier.',
    RATE_LIMIT_IMPORT: 'Limite de 5 imports par heure atteinte.',
    RATE_LIMIT_IMPORT_ANALYZE: 'Trop d\'analyses en une heure, réessaie plus tard.',
    FILE_TOO_LARGE: 'Fichier trop volumineux (2 Mo maximum).',
    FILE_TYPE: 'Seuls les fichiers .csv sont acceptés.',
    CSV_INVALID_HEADER: 'Ce fichier ne ressemble pas à un export Caisse d\'Épargne.',
};

function friendlyImportError(error) {
    const msg = error?.code && IMPORT_ERROR_MESSAGES[error.code];
    if (msg) {
        const e = new Error(msg);
        e.code = error.code;
        e.status = error.status;
        return e;
    }
    return error;
}

function csvFormData(file) {
    const fd = new FormData();
    fd.append('file', file, file.name || 'export.csv');
    return fd;
}

// Service d'import bancaire (CSV Caisse d'Épargne)
export const importService = {
    // Analyse un export : renvoie un aperçu { previewId, rows, stats, llm, warnings }
    async analyzeCsv(file) {
        try {
            const data = await apiRequest('/import/csv?dryRun=true', {
                method: 'POST',
                body: csvFormData(file),
            });
            return data.data;
        } catch (error) {
            throw friendlyImportError(error);
        }
    },

    // Valide un aperçu, avec les libellés corrigés par l'utilisateur
    async commit(previewId, overrides = []) {
        try {
            const data = await apiRequest('/import/csv/commit', {
                method: 'POST',
                body: JSON.stringify({ previewId, overrides }),
            });
            return data.data;
        } catch (error) {
            throw friendlyImportError(error);
        }
    },

    async getBatches() {
        const data = await apiRequest('/import/batches');
        return data.data?.batches || [];
    },

    async cancelBatch(batchId) {
        try {
            const data = await apiRequest(`/import/batches/${batchId}`, { method: 'DELETE' });
            return data.data;
        } catch (error) {
            throw friendlyImportError(error);
        }
    },

    async getStatus() {
        const data = await apiRequest('/import/status');
        return data.data;
    },
};

// Service des règles de libellé et de l'apprentissage
export const labelService = {
    async getRules() {
        const data = await apiRequest('/labels/rules');
        return data.data?.rules || [];
    },

    // { match, matchType?, label, emoji?, applyToExisting? } → { rule, applied }
    async createRule(rule) {
        const data = await apiRequest('/labels/rules', {
            method: 'POST',
            body: JSON.stringify(rule),
        });
        return data.data;
    },

    async createRules(rules) {
        const data = await apiRequest('/labels/rules/bulk', {
            method: 'POST',
            body: JSON.stringify({ rules }),
        });
        return data.data;
    },

    async updateRule(ruleId, rule) {
        const data = await apiRequest(`/labels/rules/${ruleId}`, {
            method: 'PUT',
            body: JSON.stringify(rule),
        });
        return data.data?.rule;
    },

    async deleteRule(ruleId) {
        await apiRequest(`/labels/rules/${ruleId}`, { method: 'DELETE' });
        return true;
    },

    // Apprentissage depuis un export : { pairs, proposals, ambiguities, skippedUserRules }
    async learn(file) {
        try {
            const data = await apiRequest('/labels/learn', {
                method: 'POST',
                body: csvFormData(file),
            });
            return data.data;
        } catch (error) {
            throw friendlyImportError(error);
        }
    },

    async getVocabulary() {
        const data = await apiRequest('/labels/vocabulary');
        return data.data?.vocabulary || [];
    },
};

// Utilitaire pour tester la connexion API
export const testConnection = async () => {
    try {
        const data = await apiRequest('/health');
        return data;
    } catch (error) {
        throw error;
    }
};

// Intercepteur global pour gérer les erreurs de session
export const setupGlobalErrorHandler = (onSessionExpired) => {
    // Cette fonction sera appelée par le contexte d'authentification
    // pour gérer les erreurs globales de session
    window.addEventListener('unhandledrejection', (event) => {
        if (event.reason instanceof APIError) {
            if (event.reason.code === 'SESSION_EXPIRED' || 
                event.reason.code === 'INVALID_TOKEN' ||
                (event.reason.status === 401 && event.reason.code !== 'INVALID_CREDENTIALS')) {
                
                console.log('Session expirée détectée, déconnexion...');
                onSessionExpired();
                event.preventDefault(); // Empêcher l'affichage de l'erreur dans la console
            }
        }
    });
};
